import {
  OBJECT_TYPES,
  WORLD_LIMITS,
  portsOf,
  validateWorld,
  type World,
  type WorldConnection,
  type WorldObject,
  type WorldObjectType,
} from '@ebu/world-schema';

/**
 * Workshop editor as pure functions (Phase 8, ADR 0014). Every change is a command on a World
 * document (the same versioned shape that is saved and exported); the editor keeps an undo/redo
 * history of whole documents. Nothing here knows about React, PlayCanvas or storage, so the 3D
 * view, the 2D plan and the tests drive exactly the same code.
 */

/** Grid step in metres. */
export const GRID = 0.5;
/** The workshop room is 10 m × 10 m; objects stay inside it. */
export const ROOM_HALF = 4.5;
export const MAX_HEIGHT = 3;
export const HISTORY_LIMIT = 50;

export type Refusal =
  | 'too-many-objects'
  | 'too-many-wires'
  | 'same-port'
  | 'already-connected'
  | 'unknown-object'
  | 'not-a-port'
  | 'invalid';

export interface EditorState {
  world: World;
  selected: string | null;
  past: readonly World[];
  future: readonly World[];
  snap: boolean;
  /** The document as last saved (null = never saved). */
  savedWorld: World | null;
  /** The document as opened (a fresh starter or a loaded project). */
  baseWorld: World;
}

export interface PortRef {
  objectId: string;
  port: string;
}

export function createEditor(world: World, saved: boolean): EditorState {
  return {
    world,
    selected: null,
    past: [],
    future: [],
    snap: true,
    savedWorld: saved ? world : null,
    baseWorld: world,
  };
}

/** Not saved yet (a fresh starter, or changes since the last save). */
export const isDirty = (s: EditorState) => s.savedWorld !== s.world;
/** Leaving would lose work: the world changed since it was opened or last saved. */
export const hasUnsavedWork = (s: EditorState) => s.world !== (s.savedWorld ?? s.baseWorld);
export const canUndo = (s: EditorState) => s.past.length > 0;
export const canRedo = (s: EditorState) => s.future.length > 0;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round = (v: number) => Math.round(v * 1000) / 1000;

export function snapValue(v: number, snap: boolean): number {
  return round(snap ? Math.round(v / GRID) * GRID : v);
}

export function placePosition(
  [x, y, z]: readonly [number, number, number],
  snap: boolean,
): [number, number, number] {
  return [
    clamp(snapValue(x, snap), -ROOM_HALF, ROOM_HALF),
    clamp(round(y), 0, MAX_HEIGHT),
    clamp(snapValue(z, snap), -ROOM_HALF, ROOM_HALF),
  ];
}

function defaultProps(type: WorldObjectType): WorldObject['props'] {
  if (OBJECT_TYPES[type].category === 'decor') {
    return type === 'poster' ? { colour: 'sun', caption: null } : { colour: 'natural' };
  }
  if (type === 'switch') return { closed: false };
  if (type === 'light-sensor') return { threshold: 0.5 };
  return {};
}

function freshId(world: World, type: WorldObjectType): string {
  const taken = new Set(world.objects.map((o) => o.id));
  for (let n = 1; ; n++) {
    const id = `${type}-${n}`;
    if (!taken.has(id)) return id;
  }
}

function freshWireId(world: World): string {
  const taken = new Set(world.connections.map((c) => c.id));
  for (let n = 1; ; n++) {
    const id = `wire-${n}`;
    if (!taken.has(id)) return id;
  }
}

type Change = (world: World) => World | Refusal;

/** Applies a change, validates the result and records history; refusals leave the state as it was. */
export function apply(
  state: EditorState,
  change: Change,
  select?: (world: World) => string | null,
): EditorState | Refusal {
  const next = change(state.world);
  if (typeof next === 'string') return next;
  if (next === state.world) return state;
  const checked = validateWorld(next);
  if (!checked.ok) return 'invalid';
  return {
    ...state,
    world: checked.world,
    past: [...state.past, state.world].slice(-HISTORY_LIMIT),
    future: [],
    selected: select ? select(checked.world) : state.selected,
  };
}

export function undo(state: EditorState): EditorState {
  const previous = state.past[state.past.length - 1];
  if (!previous) return state;
  return {
    ...state,
    world: previous,
    past: state.past.slice(0, -1),
    future: [state.world, ...state.future],
    selected: previous.objects.some((o) => o.id === state.selected) ? state.selected : null,
  };
}

export function redo(state: EditorState): EditorState {
  const next = state.future[0];
  if (!next) return state;
  return {
    ...state,
    world: next,
    past: [...state.past, state.world].slice(-HISTORY_LIMIT),
    future: state.future.slice(1),
    selected: next.objects.some((o) => o.id === state.selected) ? state.selected : null,
  };
}

export const select = (state: EditorState, id: string | null): EditorState => ({
  ...state,
  selected: id && state.world.objects.some((o) => o.id === id) ? id : null,
});

export const setSnap = (state: EditorState, snap: boolean): EditorState => ({ ...state, snap });

export function markSaved(state: EditorState, saved: World): EditorState {
  return { ...state, world: saved, savedWorld: saved };
}

// ------------------------------------------------------------------ commands

const updateObject = (world: World, id: string, patch: (o: WorldObject) => WorldObject): World => ({
  ...world,
  objects: world.objects.map((o) => (o.id === id ? patch(o) : o)),
});

export function placeObject(
  type: WorldObjectType,
  position: readonly [number, number, number],
  snap: boolean,
): Change {
  return (world) => {
    if (world.objects.length >= WORLD_LIMITS.maxObjects) return 'too-many-objects';
    const object = {
      id: freshId(world, type),
      type,
      position: placePosition(position, snap),
      rotationY: 0,
      scale: 1,
      props: defaultProps(type),
    } as WorldObject;
    return { ...world, objects: [...world.objects, object] };
  };
}

/** The id the last placeObject created (for selecting it). */
export const lastObjectId = (world: World) => world.objects[world.objects.length - 1]?.id ?? null;

export function moveObject(
  id: string,
  position: readonly [number, number, number],
  snap: boolean,
): Change {
  return (world) => {
    if (!world.objects.some((o) => o.id === id)) return 'unknown-object';
    return updateObject(world, id, (o) => ({ ...o, position: placePosition(position, snap) }));
  };
}

/** Arrow keys: one grid step (snap on) or 10 cm (snap off) along x or z. */
export function nudgeObject(id: string, dx: number, dz: number, snap: boolean): Change {
  return (world) => {
    const object = world.objects.find((o) => o.id === id);
    if (!object) return 'unknown-object';
    const step = snap ? GRID : 0.1;
    const [x, y, z] = object.position;
    return moveObject(id, [x + dx * step, y, z + dz * step], snap)(world);
  };
}

export function rotateObject(id: string, degrees: number): Change {
  return (world) => {
    if (!world.objects.some((o) => o.id === id)) return 'unknown-object';
    return updateObject(world, id, (o) => ({
      ...o,
      rotationY: round((((o.rotationY + degrees) % 360) + 360) % 360),
    }));
  };
}

export function scaleObject(id: string, scale: number): Change {
  return (world) => {
    if (!world.objects.some((o) => o.id === id)) return 'unknown-object';
    return updateObject(world, id, (o) => ({ ...o, scale: clamp(round(scale), 0.25, 4) }));
  };
}

export function setProps(id: string, props: Partial<Record<string, unknown>>): Change {
  return (world) => {
    if (!world.objects.some((o) => o.id === id)) return 'unknown-object';
    return updateObject(
      world,
      id,
      (o) => ({ ...o, props: { ...o.props, ...props } }) as WorldObject,
    );
  };
}

/** Removing an object also removes the wires attached to it. */
export function removeObject(id: string): Change {
  return (world) => {
    if (!world.objects.some((o) => o.id === id)) return 'unknown-object';
    return {
      ...world,
      objects: world.objects.filter((o) => o.id !== id),
      connections: world.connections.filter((c) => c.from.objectId !== id && c.to.objectId !== id),
    };
  };
}

export function duplicateObject(id: string, snap: boolean): Change {
  return (world) => {
    const object = world.objects.find((o) => o.id === id);
    if (!object) return 'unknown-object';
    const placed = placeObject(
      object.type,
      [object.position[0] + 1, object.position[1], object.position[2]],
      snap,
    )(world);
    if (typeof placed === 'string') return placed;
    const copyId = lastObjectId(placed)!;
    return updateObject(
      placed,
      copyId,
      (o) =>
        ({
          ...o,
          rotationY: object.rotationY,
          scale: object.scale,
          props: object.props,
        }) as WorldObject,
    );
  };
}

const samePort = (a: PortRef, b: PortRef) => a.objectId === b.objectId && a.port === b.port;
const joins = (c: WorldConnection, a: PortRef, b: PortRef) =>
  (samePort(c.from, a) && samePort(c.to, b)) || (samePort(c.from, b) && samePort(c.to, a));

export function connect(from: PortRef, to: PortRef): Change {
  return (world) => {
    for (const end of [from, to]) {
      const object = world.objects.find((o) => o.id === end.objectId);
      if (!object) return 'unknown-object';
      if (!portsOf(object.type).includes(end.port)) return 'not-a-port';
    }
    if (samePort(from, to)) return 'same-port';
    if (world.connections.some((c) => joins(c, from, to))) return 'already-connected';
    if (world.connections.length >= WORLD_LIMITS.maxConnections) return 'too-many-wires';
    return {
      ...world,
      connections: [...world.connections, { id: freshWireId(world), from, to }],
    };
  };
}

export function disconnect(id: string): Change {
  return (world) => ({ ...world, connections: world.connections.filter((c) => c.id !== id) });
}

export function rename(title: string): Change {
  return (world) => ({ ...world, title: title.trim().slice(0, 60) || world.title });
}

/** All ports in the world, for the "Connect to…" list and the plan view. */
export function allPorts(world: World): PortRef[] {
  return world.objects.flatMap((o) => portsOf(o.type).map((port) => ({ objectId: o.id, port })));
}
